import { ChevronDown, ChevronUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'

interface FrequencyStepperProps {
  value: number
  onChange: (value: number) => void
  /** Announced name of the cell, e.g. "W3 Pull ups". */
  label: string
  readOnly?: boolean
}

/**
 * Times-per-week picker for a matrix cell: a round dial showing the number,
 * whose top half steps up and bottom half steps down. It replaces the native
 * number input, whose browser spinners were too small to hit in a dense grid.
 *
 * The dial itself is the spinbutton (arrow keys work when it has focus); the
 * two halves are hidden from assistive tech so the value is announced once.
 */
export function FrequencyStepper({ value, onChange, label, readOnly = false }: FrequencyStepperProps) {
  const { t } = useTranslation()

  function step(delta: number) {
    const next = Math.max(0, value + delta)
    if (next !== value) onChange(next)
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (readOnly) return
    if (event.key === 'ArrowUp') step(1)
    else if (event.key === 'ArrowDown') step(-1)
    else if (event.key === 'Home') onChange(0)
    else return
    event.preventDefault()
  }

  // The arrows stay out of the way until the dial is pointed at; on a touch
  // screen, where nothing is ever hovered, they show faintly all the time.
  const half =
    'absolute inset-x-0 flex h-1/2 justify-center text-muted-foreground opacity-0 transition ' +
    'hover:bg-primary/10 hover:opacity-100 group-hover:opacity-50 focus-visible:opacity-100 ' +
    'focus-visible:outline-none [@media(hover:none)]:opacity-60'

  return (
    <div
      role="spinbutton"
      aria-label={label}
      aria-valuenow={value}
      aria-valuemin={0}
      aria-readonly={readOnly || undefined}
      tabIndex={readOnly ? -1 : 0}
      onKeyDown={handleKeyDown}
      className={cn(
        'group relative mx-auto size-10 select-none rounded-full border bg-background transition-colors',
        readOnly ? 'cursor-default opacity-70' : 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-0 flex items-center justify-center text-sm tabular-nums',
          value === 0 ? 'text-muted-foreground/40' : 'font-medium'
        )}
      >
        {value}
      </span>
      {!readOnly && (
        <>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            data-step="up"
            title={t('trainingPlans.builder.increase')}
            onClick={() => step(1)}
            className={cn(half, 'top-0 items-start rounded-t-full pt-px')}
          >
            <ChevronUp className="size-3" />
          </button>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            data-step="down"
            title={t('trainingPlans.builder.decrease')}
            onClick={() => step(-1)}
            className={cn(half, 'bottom-0 items-end rounded-b-full pb-px')}
          >
            <ChevronDown className="size-3" />
          </button>
        </>
      )}
    </div>
  )
}
